/**
 * PriorityList — obecný komponent pro správu prioritního seznamu položek.
 *
 * Pattern: seznam s drag&drop pořadím, toggle on/off per položka,
 * inline expand editor, badges napravo, přidat novou, smazat.
 *
 * Typické použití:
 *   - LLM profily (priority fallback)
 *   - Keycloak instance (failover auth)
 *   - Backup targety (priority zápisu)
 *   - Fallback řetězce (DNS, NTP, kdeco...)
 *
 * Usage:
 *   <PriorityList<MyType>
 *     items={items}
 *     getKey={(i) => i.id}
 *     getTitle={(i) => i.label}
 *     getSubtitle={(i) => i.url}
 *     isEnabled={(i) => i.enabled}
 *     renderEditor={(item, update) => <MyForm value={item} onChange={update} />}
 *     renderBadges={(i) => <Badge>{i.status}</Badge>}
 *     onSave={handleSave}
 *     onDelete={handleDelete}
 *     onToggle={handleToggle}
 *     onReorder={handleReorder}
 *     onAdd={handleAdd}
 *     addLabel="Přidat instanci"
 *     emptyMessage="Žádné instance"
 *   />
 */
import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronUp, GripVertical, Plus, Trash2 } from 'lucide-react';
import { type DragHandleProps, SortableList } from './SortableList';
import { Button } from './Button';
import { Toggle } from './Toggle';
import { ConfirmDialog } from './ConfirmDialog';

// ── Types ─────────────────────────────────────────────────────────────────────

/** Minimum required shape for PriorityList items */
export interface PriorityItem {
  id: string;
  enabled: boolean;
  priority: number;
}

export interface PriorityListProps<T extends PriorityItem> {
  items: T[];

  /** Key extractor (defaults to item.id). Override if id collisions possible. */
  getKey?: (item: T) => string;
  /** Primary line (e.g. label) */
  getTitle: (item: T) => string;
  /** Secondary line under title (e.g. endpoint/model) — optional */
  getSubtitle?: (item: T) => string | ReactNode;
  /** Enabled state selector — defaults to item.enabled */
  isEnabled?: (item: T) => boolean;

  /** Editor rendered when item is expanded. Call `update(patch)` to apply partial changes. */
  renderEditor: (item: T, update: (patch: Partial<T>) => void) => ReactNode;

  /** Badges/stats rendered in the right side of the collapsed row — optional */
  renderBadges?: (item: T) => ReactNode;

  /** Extra action buttons rendered next to the expand chevron (e.g. Test, Credentials) — optional */
  extraActions?: (item: T) => ReactNode;

  /** Callbacks */
  onSave: (item: T) => void;
  onDelete: (id: string) => void;
  onToggle: (id: string, enabled: boolean) => void;
  onReorder: (orderedIds: string[]) => void;
  onAdd?: () => void;

  /** UI config */
  addLabel?: string;
  emptyMessage?: string;
  /** Default expanded id (e.g. from query param) */
  initialExpandedId?: string | null;
  /** Show drag handle (default true) */
  showDragHandle?: boolean;
  /** Show delete button in expanded editor (default true) */
  allowDelete?: boolean;
  /** Allow expanding items to show editor (default true) */
  allowExpand?: boolean;
}

// ── Main component ────────────────────────────────────────────────────────────

export function PriorityList<T extends PriorityItem>({
  items,
  getKey,
  getTitle,
  getSubtitle,
  isEnabled,
  renderEditor,
  renderBadges,
  extraActions,
  onSave,
  onDelete,
  onToggle,
  onReorder,
  onAdd,
  addLabel,
  emptyMessage,
  initialExpandedId = null,
  showDragHandle = true,
  allowDelete = true,
  allowExpand = true,
}: PriorityListProps<T>) {
  const { t } = useTranslation();
  const resolvedAddLabel = addLabel ?? t('ui.add', 'Přidat');
  const resolvedEmpty = emptyMessage ?? t('ui.noItems', 'Žádné položky');
  const [expandedId, setExpandedId] = useState<string | null>(initialExpandedId);
  const [drafts, setDrafts] = useState<Record<string, T>>({});
  const [confirmDelete, setConfirmDelete] = useState<{ id: string; title: string } | null>(null);

  const keyFn = getKey ?? ((item: T) => item.id);
  const enabledFn = isEnabled ?? ((item: T) => item.enabled);

  const getDraft = (item: T): T => drafts[keyFn(item)] ?? item;

  const updateDraft = (item: T, patch: Partial<T>) => {
    setDrafts((prev) => ({
      ...prev,
      [keyFn(item)]: { ...getDraft(item), ...patch },
    }));
  };

  const saveDraft = (item: T) => {
    const draft = drafts[keyFn(item)];
    if (draft) {
      onSave(draft);
      setDrafts((prev) => {
        const next = { ...prev };
        delete next[keyFn(item)];
        return next;
      });
    }
  };

  const discardDraft = (item: T) => {
    setDrafts((prev) => {
      const next = { ...prev };
      delete next[keyFn(item)];
      return next;
    });
  };

  const hasDraft = (item: T) => !!drafts[keyFn(item)];

  if (items.length === 0 && !onAdd) {
    return <div className='text-sm text-gray-500 text-center py-6'>{resolvedEmpty}</div>;
  }

  return (
    <div className='space-y-2'>
      {items.length === 0
        ? <div className='text-sm text-gray-500 text-center py-6'>{resolvedEmpty}</div>
        : (
          <SortableList
            items={items}
            keyFn={keyFn}
            onReorder={(reordered) => onReorder(reordered.map(keyFn))}
            renderItem={(item, { dragHandleProps }) => {
              const id = keyFn(item);
              const isExpanded = expandedId === id;
              const displayed = isExpanded ? getDraft(item) : item;
              const dirty = hasDraft(item);

              return (
                <div className='border border-gray-800 rounded-xl bg-gray-900/30 overflow-hidden'>
                  {/* ── Collapsed row ───────────────────────────────────── */}
                  <div className='flex items-center gap-2 px-3 py-2'>
                    {showDragHandle && (
                      <button
                        {...dragHandleProps}
                        className='cursor-grab active:cursor-grabbing text-gray-600 hover:text-gray-400 flex-shrink-0'
                        aria-label={t('ui.move', 'Přesunout')}
                      >
                        <GripVertical size={14} />
                      </button>
                    )}

                    <Toggle
                      value={enabledFn(displayed)}
                      onSave={(en) => onToggle(id, en)}
                    />

                    <div
                      className={`flex-1 min-w-0 ${allowExpand ? 'cursor-pointer' : ''}`}
                      onClick={() => allowExpand && setExpandedId(isExpanded ? null : id)}
                    >
                      <div className='text-sm font-medium text-white truncate flex items-center gap-2'>
                        {getTitle(displayed)}
                        {dirty && (
                          <span className='text-[10px] text-amber-400'>
                            {t('ui.unsaved', '● neuloženo')}
                          </span>
                        )}
                      </div>
                      {getSubtitle && (
                        <div className='text-[11px] text-gray-500 truncate'>
                          {getSubtitle(displayed)}
                        </div>
                      )}
                    </div>

                    {renderBadges && (
                      <div className='flex items-center gap-1.5 flex-shrink-0'>
                        {renderBadges(displayed)}
                      </div>
                    )}

                    {extraActions && (
                      <div
                        className='flex items-center gap-1 flex-shrink-0'
                        onClick={(e) => e.stopPropagation()}
                      >
                        {extraActions(displayed)}
                      </div>
                    )}

                    {/* Smazat přímo v řádku — jen když je položka vypnutá.
                        Zapnutá se maže výhradně přes rozbalený editor (níž),
                        ať se nesmaže něco živého jedním klikem navíc. */}
                    {allowDelete && !enabledFn(displayed) && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setConfirmDelete({ id, title: getTitle(item) });
                        }}
                        className='text-gray-600 hover:text-red-400 transition-colors flex-shrink-0 p-1'
                        aria-label={t('common.delete', 'Smazat')}
                        title={t('common.delete', 'Smazat')}
                      >
                        <Trash2 size={13} />
                      </button>
                    )}

                    {allowExpand && (
                      <button
                        onClick={() => setExpandedId(isExpanded ? null : id)}
                        className='text-gray-500 hover:text-white transition-colors flex-shrink-0 p-1'
                        aria-label={isExpanded ? t('ui.collapse', 'Sbalit') : t('ui.expand', 'Rozbalit')}
                      >
                        {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                      </button>
                    )}
                  </div>

                  {/* ── Expanded editor ─────────────────────────────────── */}
                  {isExpanded && allowExpand && (
                    <div className='border-t border-gray-800 p-3 space-y-3 bg-gray-950/50'>
                      {renderEditor(displayed, (patch) => updateDraft(item, patch))}

                      <div className='flex items-center justify-between pt-2 border-t border-gray-800/50'>
                        <div>
                          {allowDelete && (
                            <Button
                              variant='ghost'
                              size='sm'
                              onClick={() => setConfirmDelete({ id, title: getTitle(item) })}
                            >
                              <Trash2 size={12} />
                              <span className='ml-1'>{t('common.delete', 'Smazat')}</span>
                            </Button>
                          )}
                        </div>
                        <div className='flex gap-2'>
                          {dirty && (
                            <Button
                              variant='ghost'
                              size='sm'
                              onClick={() => discardDraft(item)}
                            >
                              {t('ui.discard', 'Zahodit')}
                            </Button>
                          )}
                          <Button
                            variant='primary'
                            size='sm'
                            onClick={() => saveDraft(item)}
                            disabled={!dirty}
                          >
                            {t('common.save', 'Uložit')}
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            }}
          />
        )}

      {/* ── Add button ─────────────────────────────────────────────────── */}
      {onAdd && (
        <button
          onClick={onAdd}
          className='w-full flex items-center justify-center gap-2 py-2 border border-dashed border-gray-700 rounded-xl text-sm text-gray-500 hover:text-gray-300 hover:border-gray-500 transition-colors'
        >
          <Plus size={14} />
          {resolvedAddLabel}
        </button>
      )}

      <ConfirmDialog
        open={!!confirmDelete}
        title={t('ui.deleteItemTitle', 'Smazat položku?')}
        description={
          confirmDelete
            ? t('ui.deleteNamedConfirm', 'Smazat „{{name}}“? Tato akce je nevratná.', {
              name: confirmDelete.title,
            })
            : undefined
        }
        confirmLabel={t('common.delete', 'Smazat')}
        danger
        onConfirm={() => {
          if (confirmDelete) onDelete(confirmDelete.id);
          setConfirmDelete(null);
        }}
        onCancel={() => setConfirmDelete(null)}
      />
    </div>
  );
}

// Re-export DragHandleProps for convenience
export type { DragHandleProps };
